/**
 * Citations — the ONE trigger for sources on every health-guidance screen (Apple 1.4.1, third
 * round, 2026-10-17). Design approved by Vincent: XAI is the home for citations; one obvious
 * "Citations" control per result; INLINE everywhere (no drawer); no citation text on the cards.
 *
 *   <CitationsButton>  — result-level button in the result header: "📚 Citations & AI Reasoning (N)".
 *                        Controlled (open / onToggle) so screens that already toggle an XAI panel
 *                        keep their own state and exact behaviour.
 *   <CitationChip>     — per-item chip ("📚 Citations (n)") on an interaction card, alert, timing
 *                        slot or badge. Self-contained: expands the citations INLINE under the item.
 *
 * Failure handling (Vincent 2026-10-17: "citation retrieve errors are handled elegantly"): the
 * chip never renders an empty panel — with no per-item sources it still opens the feature's
 * standard citations (XaiPromptPanel citationsOnly mode always has baselines).
 */
import React, { useState } from 'react';
import XaiPromptPanel from './XaiPromptPanel';
import { citationCount } from '../utils/xaiHelpers';

export function CitationsButton({ open, onToggle, count, label = 'Citations & AI Reasoning', testId }) {
  return (
    <button
      type="button"
      className="cite-btn"
      aria-expanded={!!open}
      data-testid={testId || 'citations-button'}
      onClick={(e) => { e.stopPropagation(); onToggle && onToggle(); }}
    >
      <span aria-hidden="true">📚</span>
      {open ? 'Hide citations' : label}
      {!open && count > 0 && <span className="cite-btn-count">{count}</span>}
    </button>
  );
}

export function CitationChip({ sources, featureName, itemTitle = '', promptData = null, testId }) {
  const [open, setOpen] = useState(false);
  const n = citationCount(promptData, featureName, sources);
  return (
    <div className="cite-inline" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="cite-chip"
        aria-expanded={open}
        data-testid={testId || 'citation-chip'}
        onClick={() => setOpen((v) => !v)}
      >
        <span aria-hidden="true">📚</span> {open ? 'Hide citations' : `Citations (${n})`}
      </button>
      {open && (
        <XaiPromptPanel
          promptData={promptData}
          featureName={featureName}
          itemCitations={sources || []}
          itemTitle={itemTitle}
          citationsOnly={!promptData}
        />
      )}
    </div>
  );
}

/**
 * A result-level citations block for deterministic checks that have no AI prompt: the standard
 * button plus the inline citations panel. Used where a screen had NO citations control at all.
 */
export function CitationsBlock({ sources, featureName, itemTitle = '', promptData = null, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="cite-inline">
      <CitationsButton
        open={open}
        onToggle={() => setOpen((v) => !v)}
        count={citationCount(promptData, featureName, sources)}
        label={promptData ? 'Citations & AI Reasoning' : 'Citations'}
        testId={testId}
      />
      {open && (
        <XaiPromptPanel
          promptData={promptData}
          featureName={featureName}
          itemCitations={sources || []}
          itemTitle={itemTitle}
          citationsOnly={!promptData}
        />
      )}
    </div>
  );
}
